import { gql } from "graphql-request";
import { createGraphQLClient } from "./graphql.service";
import type { QueryInput } from "./entity-instance.service";
import type {
  CachedResults,
  ComparisonWindow,
  Dashboard,
  DashboardDateRange,
  Widget,
} from "@/types/dashboards";

/**
 * Backend persistence for the "Dashboard" custom entity (gated behind the
 * opt-in Dashboards feature — see template.service.ts). Each dashboard is one
 * entity instance: `name`/`description` are real columns, while the widgets and
 * date range live in a single JSON `config` column. Keeping the structure in a
 * JSON blob lets the widget schema evolve without backend migrations; typed
 * columns can be promoted later once the shape settles.
 */
interface DashboardConfig {
  widgets: Widget[];
  dateRange?: DashboardDateRange;
  comparison?: ComparisonWindow;
}

/** Raw entity-instance shape. JSON fields come back as strings (mirrors DashboardView). */
export interface DashboardResponse {
  id: string;
  name: string;
  description?: string | null;
  config?: string | null;
  /**
   * Persisted per-widget result cache. Added by the cache feature — older
   * dashboards (or entities without the field yet) return null/undefined.
   */
  cached_results?: string | null;
  created_at: string;
  updated_at: string;
}

const parseConfig = (raw?: string | null): DashboardConfig => {
  if (!raw) return { widgets: [] };
  try {
    const parsed = JSON.parse(raw) as Partial<DashboardConfig>;
    return {
      widgets: parsed.widgets ?? [],
      dateRange: parsed.dateRange,
      comparison: parsed.comparison,
    };
  } catch {
    // Corrupt/legacy config shouldn't break the whole list — fall back to empty.
    return { widgets: [] };
  }
};

const serializeConfig = (
  dashboard: Pick<Dashboard, "widgets" | "dateRange" | "comparison">,
): string =>
  JSON.stringify({
    widgets: dashboard.widgets,
    dateRange: dashboard.dateRange,
    comparison: dashboard.comparison,
  } satisfies DashboardConfig);

/**
 * Parse the `cached_results` JSON blob defensively. Anything unexpected (null,
 * corrupt JSON, wrong shape, or the field being entirely absent on a not-yet-
 * upgraded entity) collapses to an empty cache so a bad blob never breaks the
 * dashboard — mirrors `parseConfig`.
 */
const parseCachedResults = (raw?: string | null): CachedResults => {
  if (!raw) return { entries: {} };
  try {
    const parsed = JSON.parse(raw) as Partial<CachedResults>;
    const entries = parsed.entries && typeof parsed.entries === "object" ? parsed.entries : {};
    return { entries, lastRefreshedAt: parsed.lastRefreshedAt };
  } catch {
    return { entries: {} };
  }
};

/**
 * Last parse per dashboard id, so re-parsing an unchanged row REUSES the exact
 * same objects. This is load-bearing for render cost, not a micro-optimisation:
 * persisting the widget result cache writes `cached_results` back onto this row,
 * the live subscription hands that echo straight back to us, and without this
 * every widget would get a fresh (but identical) object and every chart on the
 * dashboard would re-render. Keyed by id and holding one entry each, so it stays
 * bounded by the dashboard count.
 */
interface ParsedDashboardMemo {
  config: string | null;
  cachedResults: string | null;
  name: string;
  description: string | null;
  updatedAt: string;
  dashboard: Dashboard;
}
const parsedDashboards = new Map<string, ParsedDashboardMemo>();

export const parseDashboard = (response: DashboardResponse): Dashboard => {
  const rawConfig = response.config ?? null;
  const rawCached = response.cached_results ?? null;
  const description = response.description ?? null;
  const previous = parsedDashboards.get(response.id);

  if (
    previous &&
    previous.config === rawConfig &&
    previous.cachedResults === rawCached &&
    previous.name === response.name &&
    previous.description === description &&
    previous.updatedAt === response.updated_at
  ) {
    return previous.dashboard;
  }

  // Reuse the halves whose raw JSON is byte-identical, so a cache-only write
  // leaves `widgets` referentially unchanged.
  const config =
    previous && previous.config === rawConfig
      ? {
          widgets: previous.dashboard.widgets,
          dateRange: previous.dashboard.dateRange,
          comparison: previous.dashboard.comparison,
        }
      : parseConfig(rawConfig);
  const cachedResults =
    previous && previous.cachedResults === rawCached
      ? previous.dashboard.cachedResults
      : parseCachedResults(rawCached);

  const dashboard: Dashboard = {
    id: response.id,
    name: response.name,
    description: response.description ?? undefined,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
    dateRange: config.dateRange,
    comparison: config.comparison,
    widgets: config.widgets,
    cachedResults,
  };

  parsedDashboards.set(response.id, {
    config: rawConfig,
    cachedResults: rawCached,
    name: response.name,
    description,
    updatedAt: response.updated_at,
    dashboard,
  });
  return dashboard;
};

export const DASHBOARD_FIELDS = `
  id
  name
  description
  config
  cached_results
  created_at
  updated_at
`;

const dashboardListQueryDocument = gql`
  query DashboardList {
    dashboardList(pagination: { page: 1, per_page: 1000 }) {
      data {
        ${DASHBOARD_FIELDS}
      }
    }
  }
`;

const createDashboardMutationDocument = gql`
  mutation CreateDashboard($input: CreateDashboardInput!) {
    createDashboard(input: $input) {
      data {
        ${DASHBOARD_FIELDS}
      }
    }
  }
`;

const updateDashboardMutationDocument = gql`
  mutation UpdateDashboard($input: UpdateDashboardInput!) {
    updateDashboard(input: $input) {
      data {
        ${DASHBOARD_FIELDS}
      }
    }
  }
`;

const deleteDashboardMutationDocument = gql`
  mutation DeleteDashboard($ids: [ID!]!) {
    deleteDashboard(ids: $ids) {
      data {
        success
      }
    }
  }
`;

export const fetchDashboards = async (): Promise<Dashboard[]> => {
  const client = createGraphQLClient({ endpointName: "dashboardList" });
  const result = (await client.request(dashboardListQueryDocument)) as {
    dashboardList: { data: DashboardResponse[] };
  };
  return result.dashboardList.data.map(parseDashboard);
};

export const createDashboardRemote = async (input: {
  name: string;
  description?: string;
  dateRange?: DashboardDateRange;
}): Promise<Dashboard> => {
  const client = createGraphQLClient({ endpointName: "createDashboard" });
  const result = (await client.request(createDashboardMutationDocument, {
    input: {
      name: input.name,
      description: input.description,
      config: serializeConfig({ widgets: [], dateRange: input.dateRange }),
    },
  })) as { createDashboard: { data: DashboardResponse } };
  return parseDashboard(result.createDashboard.data);
};

export const updateDashboardRemote = async (dashboard: Dashboard): Promise<Dashboard> => {
  const client = createGraphQLClient({ endpointName: "updateDashboard" });
  const result = (await client.request(updateDashboardMutationDocument, {
    input: {
      id: dashboard.id,
      name: dashboard.name,
      description: dashboard.description ?? "",
      config: serializeConfig(dashboard),
    },
  })) as { updateDashboard: { data: DashboardResponse } };
  return parseDashboard(result.updateDashboard.data);
};

/**
 * Persist ONLY the `cached_results` field of a dashboard — a partial update that
 * deliberately omits `name`/`description`/`config` so it never races with (or
 * clobbers) unsaved widget/layout edits. The generic `updateDashboard` input
 * treats every non-`id` field as optional, so a two-field payload is a valid
 * partial update.
 *
 * Best-effort by design: on a not-yet-upgraded entity the `cached_results` field
 * doesn't exist and the mutation throws. We swallow (log) that so the dashboard
 * keeps working uncached until the feature is upgraded — the caller must not
 * depend on the write succeeding.
 */
export const updateDashboardCacheRemote = async (
  id: string,
  cachedResults: CachedResults,
): Promise<void> => {
  try {
    const client = createGraphQLClient({ endpointName: "updateDashboard" });
    await client.request(updateDashboardMutationDocument, {
      input: {
        id,
        cached_results: JSON.stringify(cachedResults),
      },
    });
  } catch (error) {
    // Missing field on legacy entities / transient failure — never fatal.
    console.error("failed to persist dashboard cache", error);
  }
};

/**
 * Rename a dashboard — a partial update of ONLY `name` (mirrors
 * updateDashboardCacheRemote) so it never clobbers unsaved widget/layout edits.
 * Returns the new updatedAt.
 */
export const renameDashboardRemote = async (
  id: string,
  name: string,
): Promise<{ updatedAt: string }> => {
  const client = createGraphQLClient({ endpointName: "updateDashboard" });
  const result = (await client.request(updateDashboardMutationDocument, {
    input: { id, name },
  })) as { updateDashboard: { data: DashboardResponse } };
  return { updatedAt: result.updateDashboard.data.updated_at };
};

const dashboardMatchQueryDocument = gql`
  query DashboardMatch($filter: FilterInput!) {
    dashboardList(filter: $filter, pagination: { page: 1, per_page: 1 }) {
      data {
        id
      }
    }
  }
`;

/** Whether the dashboard record matches any of `groups` (OR-combined). */
export const dashboardMatchesAnyRemote = async (
  id: string,
  groups: QueryInput[],
): Promise<boolean> => {
  if (groups.length === 0) return false;
  const client = createGraphQLClient({ endpointName: "dashboardList" });
  const result = (await client.request(dashboardMatchQueryDocument, {
    filter: {
      query: [{ condition: { field: "id", operator: "equal", value: id } }, { or: groups }],
    },
  })) as { dashboardList: { data: { id: string }[] } };
  return result.dashboardList.data.length > 0;
};

export const deleteDashboardRemote = async (id: string): Promise<void> => {
  const client = createGraphQLClient({ endpointName: "deleteDashboard" });
  await client.request(deleteDashboardMutationDocument, { ids: [id] });
};
